import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import * as errorHandlerActions from '@/components/errorHandler/Actions';
import * as goalSettingsActions from '@/pages/goalSettings/Actions';
import * as learningPathActions from '@/pages/learningPath/Actions';
import * as sharedActions from '@/shared/Actions';
import { goalSettingsActionTypes, SUBJECT_TYPES } from '@/pages/goalSettings/Constants';
import { ROUTES_API } from '@/services/Constants';
import { generateGoalSettingsPayload } from '@/pages/goalSettings/Helpers';
import { getPlayDaysPerWeek, getUserData } from '@/pages/goalSettings/Selectors';
import { processRequest } from '@/services/Api';

import type { SagaIterator } from 'redux-saga';
import type { Subject } from '@/pages/goalSettings/types';

export default function* goalSettingsSaga(): SagaIterator {
	yield all([
		yield takeLatest(
			goalSettingsActionTypes.GET_GOAL_SETTINGS_REQUEST,
			handleGetGoalSettingsRequest,
		),
		yield takeLatest(
			goalSettingsActionTypes.GET_GOAL_DURATIONS_REQUEST,
			handleGetDurationsRequest,
		),
		yield takeLatest(
			goalSettingsActionTypes.SAVE_GOAL_SETTING_REQUEST,
			handleSaveGoalSettingsRequest,
		),
		takeLatest(
			goalSettingsActionTypes.GET_CURRENT_PRACTICE_REQUEST,
			handleGetCurrentPracticeRequest,
		),
	]);
}

export function* handleGetGoalSettingsRequest(): SagaIterator {
	try {
		const { data } = yield call(processRequest, ROUTES_API.GOAL_SUBJECTS);

		let subjects: Subject[];

		const userData = yield select(getUserData);
		const { grade_id, play_mode } = userData || {};

		if ([4, 5].includes(grade_id)) {
			subjects = data.filter((subject: any) => subject.type !== SUBJECT_TYPES.exam);
		} else {
			subjects = data;
		}

		yield put(goalSettingsActions.setPlayMode(play_mode));
		yield put(goalSettingsActions.getGoalSettingsSuccess(subjects));
	} catch (e) {
		yield put(goalSettingsActions.getGoalSettingsError());
		yield put(errorHandlerActions.handleError(e, 'page-blocking'));
	}
}

export function* handleGetDurationsRequest(): SagaIterator {
	try {
		const playDaysPerWeek: number[] = (yield select(getPlayDaysPerWeek)) || [];
		const query = playDaysPerWeek ? { play_times_per_week: playDaysPerWeek } : {};

		const { data } = yield call(
			processRequest,
			ROUTES_API.SESSION_LENGTH_OPTIONS,
			'GET',
			query,
		);

		yield put(goalSettingsActions.getGoalDurationsSuccess(data.session_length_options));
	} catch (e) {
		yield put(goalSettingsActions.getGoalDurationsError());
		yield put(errorHandlerActions.handleError(e, 'page-blocking'));
	}
}

export function* handleSaveGoalSettingsRequest({ payload }: any): SagaIterator {
	try {
		const { playDaysPerWeek, durationId, subjects } = payload;

		yield call(processRequest, ROUTES_API.USER_WEEK_GOALS, 'POST', {
			play_times_per_week: playDaysPerWeek,
		});

		if (durationId) {
			const durationBody = { user_session_length: { session_length_option_id: durationId } };
			yield call(processRequest, ROUTES_API.USER_SESSION_LENGTHS, 'POST', durationBody);
		}

		if (subjects?.length) {
			const body = generateGoalSettingsPayload(subjects);
			yield call(processRequest, ROUTES_API.USER_GOALS, 'POST', body);
		}

		const { data: userData } = yield call(processRequest, ROUTES_API.GET_USER_DATA);
		yield put(sharedActions.getUserDataSuccess(userData));

		yield put(learningPathActions.clearLearningPath());
		yield put(goalSettingsActions.saveGoalSettingsSuccess());
	} catch (e) {
		yield put(goalSettingsActions.saveGoalSettingsError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleGetCurrentPracticeRequest(): SagaIterator {
	try {
		const { data } = yield call(processRequest, ROUTES_API.USER_WEEK_GOALS_CURRENT);

		yield put(goalSettingsActions.getCurrentPracticeSuccess(data?.play_times_per_week || 0));
	} catch (e) {
		yield put(goalSettingsActions.getCurrentPracticeError());
		yield put(errorHandlerActions.handleError(e, 'page-blocking'));
	}
}
